import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Flag, ListChecks, Plus, Trash2, Users } from 'lucide-react';
import { CRMUser, Subtask } from '../../types';
import UserAvatar from '../ui/UserAvatar';
import {
  ACTION_REQUIRED_EXTERNAL_SENTINEL,
  getActionRequiredShortLabel,
} from '../../utils/actionRequiredDisplay';
import {
  groupOpsChecklistByAssignee,
  OPS_CHECKLIST_UNASSIGNED_KEY,
  sortOpsChecklistItems,
} from '../../utils/opsChecklistGroups';
import { filterOpsTaskEmployeeTeam, firstOpsTaskEmployeeUid } from '../../utils/userRoles';
import './OpsChecklistSection.css';

const GROUP_BY_USER_STORAGE_KEY = 'lctf:ops-checklist-group-by-user';

function readGroupByUserDefault(): boolean {
  try {
    return window.localStorage.getItem(GROUP_BY_USER_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

function persistGroupByUser(value: boolean): void {
  try {
    window.localStorage.setItem(GROUP_BY_USER_STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

interface OpsChecklistSectionProps {
  items: Subtask[];
  onChange: (items: Subtask[]) => void;
  teamMembers: CRMUser[];
  /** Default assignee for newly added checklist lines. */
  defaultAssigneeUid?: string;
  variant?: 'panel' | 'popover' | 'stream';
}

const OpsChecklistSection: React.FC<OpsChecklistSectionProps> = ({
  items,
  onChange,
  teamMembers,
  defaultAssigneeUid,
  variant = 'panel',
}) => {
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [groupByUser, setGroupByUser] = useState(readGroupByUserDefault);
  const [arMenuId, setArMenuId] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const arMenuRef = useRef<HTMLDivElement>(null);
  const assignableTeam = useMemo(() => filterOpsTaskEmployeeTeam(teamMembers), [teamMembers]);
  const defaultAssigneeEligible = useMemo(() => {
    if (defaultAssigneeUid && assignableTeam.some((u) => u.uid === defaultAssigneeUid)) {
      return defaultAssigneeUid;
    }
    return firstOpsTaskEmployeeUid(teamMembers, defaultAssigneeUid ? [defaultAssigneeUid] : undefined);
  }, [assignableTeam, defaultAssigneeUid, teamMembers]);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  useEffect(() => {
    if (!arMenuId) return;
    const onDown = (e: MouseEvent) => {
      if (arMenuRef.current?.contains(e.target as Node)) return;
      setArMenuId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [arMenuId]);

  const done = items.filter((s) => s.completed).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const patchItem = (id: string, patch: Partial<Subtask>) => {
    onChange(items.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const handleAdd = () => {
    const title = draft.trim();
    if (!title) return;
    const maxPos = items.reduce((m, s) => Math.max(m, s.position), 0);
    const next: Subtask = {
      id: `sub_${Date.now()}`,
      title,
      completed: false,
      position: maxPos + 1,
      ...(defaultAssigneeEligible ? { assignedTo: defaultAssigneeEligible } : {}),
    };
    onChange([...items, next]);
    setDraft('');
  };

  const toggleItem = (id: string) => {
    const item = items.find((s) => s.id === id);
    if (!item) return;
    patchItem(id, { completed: !item.completed });
  };

  const deleteItem = (id: string) => {
    onChange(items.filter((s) => s.id !== id));
    if (editingId === id) setEditingId(null);
    if (arMenuId === id) setArMenuId(null);
  };

  const startEdit = (item: Subtask) => {
    setEditingId(item.id);
    setEditTitle(item.title);
  };

  const commitEdit = () => {
    if (!editingId) return;
    const title = editTitle.trim();
    if (!title) {
      setEditingId(null);
      return;
    }
    patchItem(editingId, { title });
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const toggleGroupByUser = () => {
    setGroupByUser((prev) => {
      const next = !prev;
      persistGroupByUser(next);
      return next;
    });
  };

  const renderItem = (item: Subtask) => {
    const assignee = item.assignedTo
      ? teamMembers.find((u) => u.uid === item.assignedTo)
      : undefined;
    const arLabel = item.actionRequired
      ? getActionRequiredShortLabel(item.actionRequired, item.actionRequiredNote, teamMembers)
      : '';

    return (
      <div
        key={item.id}
        className={`ops-checklist-item${item.completed ? ' done' : ''}`}
      >
        <button
          type="button"
          className={`ops-checklist-check${item.completed ? ' checked' : ''}`}
          onClick={() => toggleItem(item.id)}
          aria-label={item.completed ? `Mark "${item.title}" incomplete` : `Mark "${item.title}" complete`}
          aria-pressed={item.completed}
        >
          {item.completed && <Check size={10} strokeWidth={3} />}
        </button>

        {editingId === item.id ? (
          <input
            ref={editInputRef}
            className="ops-checklist-edit-input"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitEdit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
            }}
            aria-label="Edit checklist item"
          />
        ) : (
          <button
            type="button"
            className="ops-checklist-title"
            onClick={() => startEdit(item)}
            title="Click to edit"
          >
            {item.title}
          </button>
        )}

        <select
          className="ops-checklist-assignee"
          value={
            item.assignedTo && assignableTeam.some((u) => u.uid === item.assignedTo)
              ? item.assignedTo
              : ''
          }
          onChange={(e) => patchItem(item.id, { assignedTo: e.target.value || undefined })}
          aria-label={`Assignee for ${item.title}`}
          title={assignee?.displayName || 'Assignee'}
        >
          <option value="">—</option>
          {assignableTeam.map((u) => (
            <option key={u.uid} value={u.uid}>
              {u.displayName}
            </option>
          ))}
        </select>

        <div
          className={`ops-checklist-ar-wrap${arMenuId === item.id ? ' pm-dropdown-host--open' : ''}`}
          ref={arMenuId === item.id ? arMenuRef : undefined}
        >
          <button
            type="button"
            className={`ops-checklist-ar${item.actionRequired ? ' ops-checklist-ar--on' : ''}`}
            onClick={() => setArMenuId((cur) => (cur === item.id ? null : item.id))}
            title={arLabel ? `Action required: ${arLabel}` : 'Set action required (AR)'}
            aria-label={arLabel ? `Action required: ${arLabel}` : 'Set action required'}
          >
            <Flag size={11} />
            {arLabel ? <span className="ops-checklist-ar-label">{arLabel}</span> : null}
          </button>
          {arMenuId === item.id && (
            <div className="pm-dropdown ops-checklist-ar-menu" role="menu">
              {item.actionRequired && (
                <button
                  type="button"
                  className="pm-dropdown-item"
                  onClick={() => {
                    patchItem(item.id, { actionRequired: undefined, actionRequiredNote: undefined });
                    setArMenuId(null);
                  }}
                >
                  Clear AR
                </button>
              )}
              {assignableTeam.map((u) => (
                <button
                  key={u.uid}
                  type="button"
                  className="pm-dropdown-item"
                  onClick={() => {
                    patchItem(item.id, { actionRequired: u.uid, actionRequiredNote: undefined });
                    setArMenuId(null);
                  }}
                >
                  {u.displayName}
                  {item.actionRequired === u.uid && <Check size={12} className="ops-detail-assign-check" />}
                </button>
              ))}
              <button
                type="button"
                className="pm-dropdown-item"
                onClick={() => {
                  const note = window.prompt('Who is action required from? (external)');
                  if (note === null) return;
                  const trimmed = note.trim();
                  if (!trimmed) {
                    patchItem(item.id, { actionRequired: undefined, actionRequiredNote: undefined });
                  } else {
                    patchItem(item.id, {
                      actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                      actionRequiredNote: trimmed,
                    });
                  }
                  setArMenuId(null);
                }}
              >
                External…
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="ops-checklist-delete"
          onClick={() => deleteItem(item.id)}
          aria-label={`Delete "${item.title}"`}
        >
          <Trash2 size={11} />
        </button>
      </div>
    );
  };

  const listContent = (() => {
    if (total === 0) {
      return <p className="ops-checklist-empty">No checklist items yet</p>;
    }
    if (!groupByUser) {
      return sortOpsChecklistItems(items).map(renderItem);
    }
    return groupOpsChecklistByAssignee(items, teamMembers).map((group) => (
      <div key={group.key} className="ops-checklist-group">
        <div className="ops-checklist-group-head">
          {group.key !== OPS_CHECKLIST_UNASSIGNED_KEY && (
            <UserAvatar
              className="ops-checklist-group-avatar"
              name={group.label}
              photoURL={teamMembers.find((u) => u.uid === group.key)?.photoURL}
              userId={group.key}
            />
          )}
          <span className="ops-checklist-group-label">{group.label}</span>
          <span className="ops-checklist-group-count">
            {group.items.filter((i) => i.completed).length}/{group.items.length}
          </span>
        </div>
        <div className="ops-checklist-group-items">
          {group.items.map(renderItem)}
        </div>
      </div>
    ));
  })();

  const rootClass =
    variant === 'popover'
      ? 'ops-checklist ops-checklist--popover'
      : variant === 'stream'
        ? 'ops-checklist ops-checklist--stream'
        : 'ops-checklist ops-checklist--panel';

  return (
    <div className={rootClass}>
      <div className="ops-checklist-header">
        <div className="ops-checklist-label-row">
          <span className="ops-checklist-label">
            <ListChecks size={12} aria-hidden />
            Checklist
          </span>
          <div className="ops-checklist-header-actions">
            <button
              type="button"
              className={`ops-checklist-view-toggle${groupByUser ? ' active' : ''}`}
              onClick={toggleGroupByUser}
              title={groupByUser ? 'Grouped by assignee' : 'Flat list'}
              aria-pressed={groupByUser}
            >
              <Users size={12} aria-hidden />
              By user
            </button>
            {total > 0 && (
              <span className="ops-checklist-count" aria-label={`${done} of ${total} complete`}>
                {done}/{total}
              </span>
            )}
          </div>
        </div>
        {total > 0 && (
          <div
            className="ops-checklist-progress"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Checklist ${pct}% complete`}
          >
            <div className="ops-checklist-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>

      <div className="ops-checklist-list">{listContent}</div>

      <div className="ops-checklist-add">
        <input
          className="ops-checklist-add-input"
          placeholder="Add checklist item…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          aria-label="New checklist item"
        />
        {draft.trim() && (
          <button
            type="button"
            className="ops-checklist-add-btn"
            onClick={handleAdd}
            aria-label="Add checklist item"
          >
            <Plus size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

export default OpsChecklistSection;
